"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CalendarRange, ChartLine as LineChart, ShieldAlert, ArrowUpRight, TrendingUp, Clock, CalendarDays } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const seasonalityData = [{
  period: "Peak Business Cycles",
  months: "Sep – Mar & Midweek (Mon–Thu)",
  focus: "IT delegations, corporate summits, global client visits",
  action: "Dynamic corporate bar pricing & strict cancellation fencing"
}, {
  period: "Shoulder & Fiscal Transition",
  months: "Apr – Jun & Sun Nights",
  focus: "Budget planning windows & project changeovers",
  action: "Length-of-stay incentives & blended bleisure packages"
}, {
  period: "Weekend & Monsoon Dynamics",
  months: "Jul – Aug & Fri–Sun",
  focus: "Domestic weekend staycations & micro-weddings",
  action: "F&B package unbundling & direct channel flash campaigns"
}];
const challenges = [{
  id: 1,
  title: "Midweek vs Weekend Yield Compression",
  subtitle: "Demand management",
  iconName: "CalendarRange",
  description: "Manage sharp drops between high-occupancy corporate weekdays (Mon–Thu) and low-rate weekends. We implement dynamic weekend staycation packages and minimum stay fencing to keep RevPAR steady across all seven days.",
  metricLabel: "Strategy focus",
  metricValue: "Midweek-to-weekend yield balancing & staycation bundling"
}, {
  id: 2,
  title: "Tech Corridor Comp-Set Undercutting",
  subtitle: "Real-time rate intelligence",
  iconName: "LineChart",
  description: "Monitor rapid rate fluctuations across Whitefield, Electronic City, and Outer Ring Road. Real-time comp-set alerts ensure your inventory captures high-intent corporate bookings without unnecessary discounting.",
  metricLabel: "Strategy focus",
  metricValue: "Micro-market tracking & automated rate fence rules"
}, {
  id: 3,
  title: "Corporate Rate Parity & OTA Leakage",
  subtitle: "Channel distribution",
  iconName: "ShieldAlert",
  description: "Protect your negotiated corporate accounts from rogue OTA undercutting and high commission costs. We establish clean direct-booking channels and parity controls across all distribution endpoints.",
  metricLabel: "Strategy focus",
  metricValue: "Corporate channel fencing & direct booking conversion"
}];
export default function Landscape() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="hospitalitylandscapebangalore" data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center mb-16 md:mb-20">
          <Badge variant="outline" className="mb-4 border-secondary/40 text-secondary-foreground">
            Market Dynamics & Yield Protection
          </Badge>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground">
            Navigating the hospitality landscape of Bangalore
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Bangalore presents a fast-moving hospitality market driven by high-yield weekday corporate demand, tech corridor events, and shifting weekend staycation patterns. Sunil Singh structures disciplined revenue models tailored specifically for business hotels, executive suites, and boutique stays across the Silicon Valley of India.
          </p>
        </div>

        {/* Seasonality Insights Block */}
        <div className="mb-14 rounded-lg border border-border bg-card p-6 md:p-8 shadow-sm">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-6 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="inline-flex size-10 items-center justify-center rounded-md bg-secondary/10 text-secondary-foreground">
                <CalendarDays className="size-5 text-chart-2" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-foreground font-serif">Bangalore Hospitality Seasonality & Demand Cycles</h2>
                <p className="text-xs sm:text-sm text-muted-foreground">Strategic pacing and demand calibration across key micro-market shifts</p>
              </div>
            </div>
            <Badge variant="secondary" className="w-fit text-xs font-mono">
              Corporate & Leisure Calibration
            </Badge>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-6">
            {seasonalityData.map((cycle, index) => <div key={cycle.period} data-index={index} className="rounded-md border border-border/70 bg-muted/30 p-4 flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-secondary uppercase tracking-wider">
                      {cycle.period}
                    </span>
                    <Clock className="size-3.5 text-muted-foreground" />
                  </div>
                  <p className="text-sm font-bold text-foreground mb-2">
                    {cycle.months}
                  </p>
                  <p className="text-xs text-muted-foreground leading-relaxed mb-4">
                    {cycle.focus}
                  </p>
                </div>
                <div className="pt-3 border-t border-border/50">
                  <span className="text-[11px] font-semibold text-foreground block mb-0.5">Strategy Action:</span>
                  <p className="text-xs text-secondary-foreground font-medium">{cycle.action}</p>
                </div>
              </div>)}
          </div>
        </div>

        {/* 3-Column Card Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {challenges.map((item, index) => <motion.div key={item.id} data-index={index} variants={cardVariants} className="h-full flex">
              <Card className="flex flex-col justify-between w-full border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-300">
                <CardHeader className="gap-3">
                  <div className="flex items-center justify-between">
                    <div className="inline-flex size-12 items-center justify-center rounded-md bg-secondary/10 text-secondary-foreground">
                      {item.iconName === "CalendarRange" && <CalendarRange className="size-6 text-chart-2" />}
                      {item.iconName === "LineChart" && <LineChart className="size-6 text-chart-2" />}
                      {item.iconName === "ShieldAlert" && <ShieldAlert className="size-6 text-chart-2" />}
                    </div>
                    <span className="text-xs font-mono font-medium text-muted-foreground tracking-wider uppercase">
                      0{index + 1}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-secondary">
                      {item.subtitle}
                    </span>
                    <CardTitle className="font-serif text-xl sm:text-2xl font-bold mt-1.5 text-foreground leading-snug">
                      {item.title}
                    </CardTitle>
                  </div>
                </CardHeader>

                <CardContent className="space-y-6 pt-2">
                  <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                    {item.description}
                  </CardDescription>

                  <div className="rounded-md border border-border/60 bg-muted/40 p-3.5 text-xs">
                    <p className="font-semibold text-foreground mb-1">
                      {item.metricLabel}
                    </p>
                    <p className="text-muted-foreground">
                      {item.metricValue}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Section Bottom CTA / Footnote */}
        <div className="mt-14 md:mt-18 pt-8 border-t border-border/70 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground text-center sm:text-left">
            Need a tailored revenue assessment for your Bangalore hotel or boutique stay?
          </p>
          <Button asChild variant="default" className="bg-primary text-primary-foreground hover:bg-primary/90">
            <Link to="#" className="inline-flex items-center gap-2">
              <span>Book a Revenue Audit</span>
              <ArrowUpRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}